"use client";

import { DiceNodeGraph } from "@/app/(articles)/(learn)/solved/DiceNodeGraph.js";
import { useMemo, useState } from "react";
import { DiceType } from "@rosette/ts/src/royalur/model/dice/DiceType.js";
import { DeviceInfoProvider, useIsMobile } from "@rosette/web_components/src/accessibility/DeviceInfoContext.js";
import { useAnimationTimer } from "@rosette/web_utils/src/useAnimationTimer.js";
import { useOnClient } from "@rosette/web_utils/src/useOnClient.js";


function InternalDiceWidget() {
    const isMobile = useIsMobile();
    const dice = useMemo(() => DiceType.FOUR_BINARY.createDice(), []);
    const [diceRolls, setDiceRolls] = useState<(number | null)[]>([]);

    const numDiceRolls = (isMobile ? 3 : 4);

    const update = useMemo(() => {
        // State.
        const diceRolls: (number | null)[] = [];
        let randomTrials = 0;

        // Animation update function.
        return () => {
            if (diceRolls[diceRolls.length - 1] === null) {
                diceRolls[diceRolls.length - 1] = dice.roll().value();
            } else if (diceRolls.length < numDiceRolls) {
                diceRolls.push(null);
            } else {
                diceRolls.splice(0, diceRolls.length);
                if (randomTrials < 5) {
                    for (let index = 0; index < numDiceRolls; ++index) {
                        diceRolls.push(dice.roll().value());
                    }
                    randomTrials += 1;
                } else {
                    randomTrials = 0;
                }
            }
            // Make a copy so we get a new reference to tell React that diceRolls changed.
            setDiceRolls([...diceRolls]);
        };
    }, [dice, setDiceRolls, numDiceRolls]);

    const animRef = useAnimationTimer(update, 1000);

    const possiblePaths = Math.pow(5, diceRolls.length);
    return (
        <div className="mb-6" ref={animRef}>
            <DiceNodeGraph
                diceRolls={diceRolls}
                numColumns={numDiceRolls + 1} />

            <p className="pt-4 text-center">
                {`${possiblePaths.toFixed(0)} possibilit${possiblePaths !== 1 ? "ies" : "y"}`}
                {" after "}
                {`${diceRolls.length} dice roll${diceRolls.length !== 1 ? "s" : ""}`}
            </p>
        </div>
    );
}


export function DiceWidget() {
    const onClient = useOnClient();
    return (
        <DeviceInfoProvider>
            {onClient && (
                <InternalDiceWidget />
            )}
        </DeviceInfoProvider>
    );
}
